import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { Checkbox } from '@/components/common/Checkbox';
import { useFilters } from '@/context/FiltersContext';

interface BrandFilterProps {
  className?: string;
  stations?: Array<{ brand: string }>;
}

export function BrandFilter({ className, stations = [] }: BrandFilterProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { selectedBrands, toggleBrand, selectAllBrands, clearBrands } = useFilters();

  // Calculate brand counts from stations
  const brandCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const station of stations) {
      const count = counts.get(station.brand) || 0;
      counts.set(station.brand, count + 1);
    }
    return counts;
  }, [stations]);

  // Sort brands by count (descending)
  const sortedBrands = useMemo(() => {
    return [...brandCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([brand]) => brand);
  }, [brandCounts]);

  const hasStations = stations.length > 0;
  const allBrandsSelected = sortedBrands.length > 0 && sortedBrands.every((b) => selectedBrands.includes(b));
  const noBrandsSelected = selectedBrands.length === 0;
  const filterActive = selectedBrands.length > 0 && selectedBrands.length < sortedBrands.length;

  return (
    <div className={className}>
      {/* Header with expand toggle */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-1 group"
      >
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-gray-700">Filter by Brand</h3>
          {filterActive && (
            <span className="px-1.5 py-0.5 bg-brand-100 text-brand-700 text-xs font-medium rounded">
              {selectedBrands.length}
            </span>
          )}
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <svg
            className="w-4 h-4 text-gray-400 group-hover:text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </motion.div>
      </button>

      {/* Expandable content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-3 space-y-3">
              {!hasStations ? (
                <p className="text-sm text-gray-500 italic">
                  Search for a location to see available brands
                </p>
              ) : (
                <>
                  {/* Select all / Clear all */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => selectAllBrands(sortedBrands)}
                      disabled={allBrandsSelected}
                      className={clsx(
                        'text-xs font-medium px-2 py-1 rounded transition-colors',
                        allBrandsSelected
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-brand-600 hover:bg-brand-50'
                      )}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={clearBrands}
                      disabled={noBrandsSelected}
                      className={clsx(
                        'text-xs font-medium px-2 py-1 rounded transition-colors',
                        noBrandsSelected
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-600 hover:bg-gray-100'
                      )}
                    >
                      Clear all
                    </button>
                  </div>

                  {/* Brand list */}
                  <div className="grid grid-cols-1 gap-1.5 max-h-48 overflow-y-auto pr-1">
                    {sortedBrands.map((brand) => (
                      <Checkbox
                        key={brand}
                        checked={selectedBrands.includes(brand)}
                        onChange={() => toggleBrand(brand)}
                        label={
                          <span className="flex items-center justify-between w-full">
                            <span className="capitalize">{brand.toLowerCase()}</span>
                            <span className="text-xs text-gray-400 ml-2">
                              ({brandCounts.get(brand)})
                            </span>
                          </span>
                        }
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
